import { Context } from "@/interfaces/sdk";
import { QuizForm, QuizFormUpdate } from "@/interfaces/sdk-quiz";
import fetch from "@mv/flow-shared-utils/functions/fetch";
import { handleErrors } from "@/utils/handle-errors";
import { tokenInfo } from "../login/types";
import { removeSelectedQuiz, setNewQuiz, setQuizzes, setQuizPatterns, setQuizAttachmentTypes } from "./types";

export const loadQuizzesByEnvironment = () => ({ type: "loadQuizzesByEnvironment" });
export const loadQuizById = (quizId: number) => ({ type: "loadQuizById", quizId });
export const createNewQuiz = (quiz: QuizForm) => ({ type: "createNewQuiz", quiz });
export const updateQuiz = (quiz: QuizFormUpdate) => ({ type: "updateQuiz", quiz });
export const deleteQuiz = (quizId: number) => ({ type: "deleteQuiz", quizId });
export const loadQuizPatterns = () => ({ type: "loadQuizPatterns" });
export const loadBotConnectionData = (environmentId: number) => ({ type: "loadBotConnectionData", environmentId });
export const loadAttachmentTypes = () => ({ type: "loadAttachmentTypes" });
export const loadInitialProcessConfig = () => ({ type: "loadInitialProcessConfig" });
export const loadXmlFromCamunda = (definitionId: string) => ({ type: "loadXmlFromCamunda", definitionId });
export const loadAllProcessesByEnvironment = () => ({ type: "loadAllProcessesByEnvironment" });
export const saveInitialProcess = (definitionId: string) => ({ type: "saveInitialProcess", definitionId });
export const updateInitialProcess = (id: number, definitionId: string) => ({ type: "updateInitialProcess", id, definitionId });
export const removeInitialProcess = () => ({ type: "removeInitialProcess" });
export const exportQuiz = (quizId: number) => ({ type: "exportQuiz", quizId });
export const importQuiz = (file: File) => ({ type: "importQuiz", file });

export const actions = {
  loadQuizzesByEnvironment(context: Context) {
    const environmentId = (context.rootGetters[tokenInfo] as any).environment.id;
    return fetch(`bot-api/v1/quiz/environment/${environmentId}`)
      .then(handleErrors)
      .then((data) => {
        context.commit(setQuizzes, data);
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  loadQuizById(context: Context, { quizId }: { quizId: number }) {
    return fetch(`bot-api/v1/quiz/${quizId}`)
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },
  createNewQuiz(context: Context, { quiz }: { quiz: QuizForm }) {
    return fetch("bot-api/v1/quiz", {
      method: "POST",
      body: JSON.stringify(quiz),
    })
      .then(handleErrors)
      .then((data) => {
        context.commit(setNewQuiz, data);
      })
      .catch((err) => {
        throw err;
      });
  },
  updateQuiz(context: Context, { quiz }: { quiz: QuizFormUpdate }) {
    return fetch("bot-api/v1/quiz", {
      method: "PUT",
      body: JSON.stringify(quiz),
    })
      .then(handleErrors)
      .then((data) => {
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  deleteQuiz(context: Context, { quizId }: { quizId: number }) {
    return fetch(`bot-api/v1/quiz/${quizId}`, {
      method: "DELETE",
    })
      .then(handleErrors)
      .then(() => {
        context.commit(removeSelectedQuiz, quizId);
      })
      .catch((err) => {
        throw err;
      });
  },
  loadQuizPatterns(context: Context) {
    return fetch("bot-api/v1/pattern")
      .then(handleErrors)
      .then((data) => {
        context.commit(setQuizPatterns, data);
      })
      .catch((err) => {
        throw err;
      });
  },
  loadBotConnectionData(context: Context, { environmentId }: { environmentId: number }) {
    return fetch(`bot-api/v1/configuration/qrcode?environmentId=${environmentId}`)
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },
  loadAttachmentTypes(context: Context) {
    return fetch("bot-api/v1/attachment")
      .then(handleErrors)
      .then((data) => {
        context.commit(setQuizAttachmentTypes, data);
      })
      .catch((err) => {
        throw err;
      });
  },
  loadInitialProcessConfig(context: Context) {
    const environmentId = (context.rootGetters[tokenInfo] as any).environment.id;
    return fetch(`bot-api/v1/initial-process/environment/${environmentId}`)
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },
  loadXmlFromCamunda(context: Context, { definitionId }: { definitionId: string }) {
    return fetch(`flow/rest/process-definition/key/${definitionId}/xml`)
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },
  loadAllProcessesByEnvironment(context: Context) {
    const environmentId = (context.rootGetters[tokenInfo] as any).environment.id;
    return fetch(`bot-api/v1/initial-process/list-process/environment/${environmentId}`)
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },
  saveInitialProcess(context: Context, { definitionId }: { definitionId: string }) {
    const environmentId = (context.rootGetters[tokenInfo] as any).environment.id;
    return fetch("bot-api/v1/initial-process", {
      method: "POST",
      body: JSON.stringify({
        environment: {
          id: environmentId,
        },
        processDefinitionId: definitionId,
      }),
    })
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },
  updateInitialProcess(context: Context, { id, definitionId }: { id: number; definitionId: string }) {
    const environmentId = (context.rootGetters[tokenInfo] as any).environment.id;
    return fetch("bot-api/v1/initial-process", {
      method: "PUT",
      body: JSON.stringify({
        environment: {
          id: environmentId,
        },
        id,
        processDefinitionId: definitionId,
      }),
    })
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },
  removeInitialProcess(context: Context) {
    const environmentId = (context.rootGetters[tokenInfo] as any).environment.id;
    return fetch(`bot-api/v1/initial-process/environment/${environmentId}`, {
      method: "DELETE",
    })
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },
  importQuiz(context: Context, { file }: { file: File }) {
    const enviromentId = (context.rootGetters[tokenInfo] as any).environment.id;

    const FORM_DATA = new FormData();

    FORM_DATA.append("file", file);

    return fetch(
      `bot-api/v1/quiz/import/${enviromentId}`,
      {
        method: "POST",
        body: FORM_DATA,
      },
      true
    )
      .then(handleErrors)
      .then((data) => {
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  exportQuiz(context: Context, { quizId }: { quizId: number }) {
    return fetch(`bot-api/v1/quiz/export/${quizId}`)
      .then(async (res: any) => {
        if (!res.ok) {
          res = await res.json();
          throw new Array(res.message);
        }
        return res.blob();
      })
      .then((res) => res)
      .catch((err) => {
        throw err;
      });
  },
};
